import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  calculateVat,
  type VatCalculation,
  type VatCalculationError,
  type VatMode,
} from '@/pages/vat-calculator/vat-calculator'
import {
  getVatRatesAvailability,
  VAT_RATE_MANIFEST,
  type VatRate,
} from '@/pages/vat-calculator/vat-rates'
import { useOnSettled, useSettledValue } from '@/shared/use-settled'
import { vatCalculatorRoute } from '@/shared/utility-registry'

export const VAT_ANALYTICS_CONTEXT = {
  app: 'calcdeck',
  domain: 'calcdeck.ru',
  route: vatCalculatorRoute.path,
  utility: vatCalculatorRoute.analyticsKey,
  processingMode: 'local',
} satisfies ToolAnalyticsContext

interface ModeOption {
  key: VatMode
  title: string
  shortTitle: string
}

const MODES: ModeOption[] = [
  {
    key: 'add',
    title: 'Начислить НДС',
    shortTitle: 'Начислить',
  },
  {
    key: 'extract',
    title: 'Выделить НДС',
    shortTitle: 'Выделить',
  },
  {
    key: 'words',
    title: 'Сумма прописью',
    shortTitle: 'Прописью',
  },
]

const RATE_LABELS: Record<VatRate, string> = {
  0: '0% · ограниченная применимость',
  5: '5% · специальная ставка УСН',
  7: '7% · специальная ставка УСН',
  10: '10% · ограниченная применимость',
  22: '22% · общая ставка',
}

const AMOUNT_LABELS: Record<VatMode, string> = {
  add: 'Сумма без НДС',
  extract: 'Сумма с НДС',
  words: 'Сумма в рублях и копейках',
}

interface VatCalculatorPageProps {
  now?: Date
  initialAmount?: string
}

export function VatCalculatorPage({ now, initialAmount = '' }: VatCalculatorPageProps) {
  const availability = getVatRatesAvailability(now)
  const [mode, setMode] = useState<VatMode>(() => availability.available ? 'add' : 'words')
  const [amount, setAmount] = useState(initialAmount)
  const [rate, setRate] = useState<VatRate>(22)
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')

  const calculation = useMemo<VatCalculation | null>(() => {
    // A trailing separator only means the kopecks are still being typed.
    const draft = amount.trim().replace(/[,.]$/, '')
    if (draft === '') return null
    if (mode !== 'words' && !availability.available) {
      return { status: 'error', error: 'unsupported_rate' }
    }
    return calculateVat(mode, draft, rate)
  }, [amount, availability.available, mode, rate])

  useEffect(() => setCopyStatus('idle'), [calculation])
  useOnSettled(calculation, reportVatCalculation)

  const selectMode = useCallback((nextMode: VatMode) => {
    setMode(nextMode)
  }, [])

  const navigateModes = useCallback((event: KeyboardEvent<HTMLButtonElement>, currentMode: VatMode) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return
    }

    const enabledModes = MODES.filter((option) => (
      option.key === 'words' || availability.available
    ))
    const currentIndex = enabledModes.findIndex((option) => option.key === currentMode)
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? enabledModes.length - 1
        : event.key === 'ArrowRight'
          ? (currentIndex + 1) % enabledModes.length
          : (currentIndex - 1 + enabledModes.length) % enabledModes.length
    const nextMode = enabledModes[nextIndex]?.key
    if (!nextMode) {
      return
    }

    event.preventDefault()
    selectMode(nextMode)
    document.querySelector<HTMLButtonElement>(`[data-vat-mode="${nextMode}"]`)?.focus()
  }, [availability.available, selectMode])

  const updateAmount = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setAmount(event.target.value)
  }, [])

  const reset = useCallback(() => {
    setMode(availability.available ? 'add' : 'words')
    setAmount('')
    setRate(22)
  }, [availability.available])

  const copyResult = useCallback(async () => {
    if (calculation?.status !== 'success' || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }

    const text = calculation.result.mode === 'words'
      ? calculation.result.wording ?? ''
      : calculation.result.lines
        .map((line) => `${line.label}: ${line.formatted} ₽ — ${line.wording}`)
        .join('\n')
    try {
      await navigator.clipboard.writeText(text)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [calculation])

  const liveError = calculation?.status === 'error' ? calculation.error : null
  const settledError = useSettledValue(liveError)
  const error = liveError === null ? null : settledError
  const result = calculation?.status === 'success' ? calculation.result : null

  return (
    <div className="ym-hide-content mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-blue-800 via-indigo-500 to-cyan-400" />
        <div className="grid min-w-0 lg:grid-cols-[minmax(0,1.04fr)_minmax(320px,0.96fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-4 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="grid min-w-0 grid-cols-3 gap-1 rounded-lg border bg-background/70 p-1" role="tablist" aria-label="Режим калькулятора НДС">
              {MODES.map((option) => {
                const disabled = option.key !== 'words' && !availability.available
                return (
                  <button
                    key={option.key}
                    data-vat-mode={option.key}
                    type="button"
                    role="tab"
                    aria-label={option.title}
                    aria-selected={mode === option.key}
                    aria-controls="vat-workspace"
                    tabIndex={mode === option.key ? 0 : -1}
                    disabled={disabled}
                    onClick={() => selectMode(option.key)}
                    onKeyDown={(event) => navigateModes(event, option.key)}
                    className={cn(
                      'min-h-10 min-w-0 rounded-md px-1 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-45',
                      mode === option.key
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    {option.shortTitle}
                  </button>
                )
              })}
            </div>

            {!availability.available && (
              <div role="status" className="rounded-md border border-amber-400/50 bg-amber-50 p-3 text-sm leading-6 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">
                <strong>Ставки проверяются.</strong> Начисление и выделение НДС временно недоступны. Режим суммы прописью продолжает работать.
                {availability.sourceUrl && (
                  <> <a className="font-medium underline underline-offset-4" href={availability.sourceUrl}>Официальный источник</a>.</>
                )}
              </div>
            )}

            <div id="vat-workspace" className="min-w-0 sm:rounded-md sm:border sm:bg-background/70 sm:p-4 sm:shadow-inner">
              <label htmlFor="vat-amount" className="text-sm font-medium">
                {AMOUNT_LABELS[mode]}
              </label>
              <input
                id="vat-amount"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={amount}
                onChange={updateAmount}
                placeholder="Например, 100 000,00"
                aria-invalid={error === 'invalid_input' || error === 'size_limit'}
                aria-describedby={
                  error === 'invalid_input' || error === 'size_limit'
                    ? 'vat-amount-help vat-input-error'
                    : 'vat-amount-help'
                }
                className="ym-disable-keys mt-1 min-h-11 w-full min-w-0 rounded-md border bg-card px-3 py-2 font-mono text-base tabular-nums outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />

              {mode !== 'words' && (
                <fieldset className="mt-3" disabled={!availability.available}>
                  <legend className="text-sm font-medium">Ставка НДС</legend>
                  <div className="mt-1 grid min-w-0 grid-cols-5 gap-1">
                    {[...VAT_RATE_MANIFEST].sort((a, b) => b.rate - a.rate).map((source) => (
                      <label
                        key={source.rate}
                        className={cn(
                          'flex min-h-10 min-w-0 cursor-pointer items-center justify-center rounded-md border text-sm font-semibold tabular-nums transition-colors focus-within:ring-2 focus-within:ring-ring',
                          rate === source.rate
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'bg-card hover:border-primary/40',
                        )}
                      >
                        <input
                          type="radio"
                          name="vat-rate"
                          value={source.rate}
                          checked={rate === source.rate}
                          onChange={() => setRate(source.rate)}
                          aria-label={RATE_LABELS[source.rate]}
                          className="sr-only"
                        />
                        {source.rate}%
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{RATE_LABELS[rate]}</p>
                </fieldset>
              )}

              {error && (
                <div id="vat-input-error" role="alert" className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {vatErrorText(error)}
                </div>
              )}
            </div>
          </div>

          <div className="min-w-0 bg-background/55 p-4 sm:p-6">
            <div className="text-sm font-medium text-muted-foreground">Результат для документа</div>
            <div aria-live="polite">
            {result?.mode === 'words' ? (
              <div className="mt-2 rounded-md border border-primary/35 bg-card p-4 shadow-sm sm:mt-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Сумма прописью</div>
                <output className="mt-3 block break-words text-lg font-semibold leading-8">
                  {result.wording}
                </output>
              </div>
            ) : result ? (
              <div className="mt-2 space-y-2 sm:mt-4 sm:space-y-3">
                {result.lines.map((line) => (
                  <div key={line.key} className="min-w-0 rounded-md border bg-card p-3 shadow-sm sm:p-4">
                    <div className="text-sm font-medium text-muted-foreground">{line.label}</div>
                    <output className="mt-1 block break-words font-mono text-xl font-bold tabular-nums sm:text-2xl">
                      {line.formatted} ₽
                    </output>
                    <div className="mt-2 break-words text-sm leading-6">{line.wording}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Введите сумму: результат появится здесь сразу.
              </p>
            )}
            </div>

            {result && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={copyResult}>Скопировать расчёт</Button>
                <Button variant="ghost" onClick={reset}>Сбросить</Button>
                <span role="status" className="text-sm text-primary">
                  {copyStatus === 'copied' ? 'Расчёт скопирован.' : ''}
                </span>
                <span role="alert" className="text-sm text-destructive">
                  {copyStatus === 'error' ? 'Не удалось скопировать расчёт.' : ''}
                </span>
              </div>
            )}

            <p id="vat-amount-help" className="mt-4 text-xs leading-5 text-muted-foreground">
              Сумма до 999 999 999 999 999,99 ₽, разделитель копеек: запятая или точка.
            </p>
            {availability.available && (
              <p id="vat-source-status" className="mt-1 text-xs leading-5 text-muted-foreground">
                Ставки проверены {formatVerifiedDate(availability.lastVerifiedAt)} по данным{' '}
                <a className="font-medium text-foreground underline underline-offset-4" href={availability.sourceUrl}>
                  ФНС
                </a>.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard
          title="Только арифметика"
          text="Калькулятор выполняет только арифметику по выбранной вами ставке. Он не определяет, обязаны ли вы платить НДС и какая ставка применима к операции. Проверьте ставку по данным ФНС или у специалиста."
        />
        <InfoCard
          title="Точное округление"
          text="НДС округляется один раз до копейки по правилу half-up, после чего итог получается сложением или вычитанием без расхождений."
        />
        <InfoCard
          title="Локальная обработка"
          text="Расчёт выполняется в браузере. Введённая сумма и текст прописью не отправляются на сервер."
        />
      </section>
    </div>
  )
}

function reportVatCalculation(calculation: VatCalculation | null): void {
  if (!calculation) return
  if (calculation.status === 'success') {
    startToolOperation(VAT_ANALYTICS_CONTEXT).success(0)
  } else if (calculation.error === 'calculation_error') {
    startToolOperation(VAT_ANALYTICS_CONTEXT).error('processing_failed')
  } else {
    trackToolRejection(VAT_ANALYTICS_CONTEXT, vatAnalyticsError(calculation.error))
  }
}

export function vatAnalyticsError(error: VatCalculationError): ToolErrorCode {
  if (error === 'size_limit') return 'size_limit'
  if (error === 'unsupported_rate') return 'unsupported_input'
  if (error === 'calculation_error') return 'processing_failed'
  return 'invalid_input'
}

function vatErrorText(error: VatCalculationError): string {
  if (error === 'size_limit') return 'Сумма или один из результатов превышает допустимый предел.'
  if (error === 'unsupported_rate') return 'Операции со ставками временно недоступны.'
  if (error === 'calculation_error') return 'Не удалось выполнить расчёт. Попробуйте ещё раз.'
  return 'Введите сумму без знака валюты: до 15 цифр, при необходимости — одна или две цифры копеек.'
}

function formatVerifiedDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number)
  const monthNames = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
  ]
  return `${day} ${monthNames[(month ?? 1) - 1]} ${year} года`
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="text-sm font-semibold tracking-normal">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}
